import * as React from "react";

import { Alert, Button, Checkbox, FormField, Input } from "Components/ui";
import { LinkSettingsInput, TenantLinkStatus } from "api/teleopdLink";
import { getApiErrorMessage } from "api/apiError";
import DialogShell from "Routes/Billing/Components/DialogShell";

const URL_PATTERN = /^https?:\/\/[^\s/?#]+(?::\d+)?(\/\S*)?$/i;

export const baseUrlError = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.length > 2048 || !URL_PATTERN.test(trimmed)) {
    return "Use an http(s) address, e.g. https://client.example.com";
  }
  return null;
};

interface LinkSettingsModalProps {
  link: TenantLinkStatus;
  onSave: (input: LinkSettingsInput) => Promise<unknown>;
  onClose: () => void;
}

/** Edit the TeleOpd address override and the private-network allowance. */
const LinkSettingsModal: React.FC<LinkSettingsModalProps> = ({ link, onSave, onClose }) => {
  const [baseUrl, setBaseUrl] = React.useState<string>(link.storedBaseUrl || "");
  const [allowPrivate, setAllowPrivate] = React.useState<boolean>(
    Boolean(link.allowPrivateNetwork)
  );
  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const urlError = baseUrlError(baseUrl);

  const handleSave = async () => {
    if (urlError) {
      return;
    }
    const input: LinkSettingsInput = {};
    const nextUrl = baseUrl.trim() || null;
    if (nextUrl !== (link.storedBaseUrl || null)) {
      input.baseUrl = nextUrl;
    }
    if (allowPrivate !== Boolean(link.allowPrivateNetwork)) {
      input.allowPrivateNetwork = allowPrivate;
    }
    if (Object.keys(input).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(input);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <DialogShell
      title="TeleOpd address"
      subtitle="Where SAP sends test calls, role lookups and sync nudges."
      size="sm"
      onClose={saving ? () => undefined : onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={Boolean(urlError)}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField
          label="Base URL override"
          id="link-base-url"
          error={urlError || undefined}
          hint={
            link.tenantUri ? (
              <>
                Leave empty to use the client&apos;s URI (
                <span className="font-mono">{link.tenantUri}</span>).
              </>
            ) : (
              "This client has no URI on record, so set the TeleOpd address here."
            )
          }
        >
          <Input
            type="url"
            value={baseUrl}
            placeholder={link.tenantUri || "https://client.example.com"}
            onChange={(event) => setBaseUrl(event.target.value)}
            className="font-mono"
          />
        </FormField>
        <div className="rounded-control border border-border px-3 py-2.5">
          <Checkbox
            checked={allowPrivate}
            onCheckedChange={(checked) => setAllowPrivate(checked === true)}
            label="Allow private network"
            description="Only for deployments inside your own network (10.x, 192.168.x, localhost). Off protects SAP from being pointed at internal services."
          />
        </div>
        {error && (
          <Alert tone="danger" title="Not saved">
            {error}
          </Alert>
        )}
      </div>
    </DialogShell>
  );
};

export default LinkSettingsModal;
